import './FilterList2.scoped.scss'
import fuzzysort from 'fuzzysort'
import { isMobile, isMacOs } from 'react-device-detect'
import { stemmer } from 'stemmer'
import { trackEvent } from 'Shared/lib/posthog'

const stemQuery = (q: string): string => q.split(/\s+/).map(stemmer).join(' ')

type Category = {
  name: string
  parent: string
  icon: string
  subheading?: string
}

type Mode = {
  name: string
  group?: string
  defaults: Record<string, any>
  quickOrder?: number | null
  newCategory?: string
}

type Filter = {
  fields: Record<string, any>
  presentation: {
    id: string
    name: string
    group?: string
    newCategory?: string
    isNew?: boolean
    quickOrder?: number | null
    keywords?: string[]
    hidden?: boolean
  }
  modes?: Mode[]
  _mode?: Mode
  customFieldId?: number
  kind?: string
}

const filterRowKey = (f: Filter, prefix: string, i: number) =>
  `${prefix}-${f.presentation.id}-${f._mode?.name || ''}-${f.customFieldId || i}`

function renderFilterName(name: string) {
  const parts = name.split(/(\([^)]+\))/)
  if (parts.length === 1) return <>{name}</>
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('(') ? <span key={i} className="dark-grey">{part}</span> : part,
      )}
    </>
  )
}

function PickerSearchInput({ search, setSearch, close, inputRef }) {
  const isModifierKeyPressed = (e: React.KeyboardEvent) => e.metaKey || e.ctrlKey

  return (
    <div className="picker-search">
      <span className="search-icon">
        <SvgIconsSearch />
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Type to search all filters..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (search) setSearch('')
            else close()
          }
          if (e.key === 'k' && isModifierKeyPressed(e)) {
            e.preventDefault()
            ;(e.target as HTMLInputElement).select()
          }
        }}
      />
      {search ? (
        <span className="clear" onClick={() => setSearch('')}>
          <SvgIconsCross />
        </span>
      ) : (
        <span className="cmd-k">{isMacOs ? '⌘K' : 'Ctrl+K'}</span>
      )}
    </div>
  )
}

function SearchResults({
  isAwaitingDebounce,
  hasNoSearchResults,
  searchResults,
  debouncedSearch,
  pickFilter,
}) {
  return (
    <div className="search-results">
      {isAwaitingDebounce ? (
        <div className="empty">Searching…</div>
      ) : hasNoSearchResults ? (
        <div className="empty">No filters match "{debouncedSearch}"</div>
      ) : (
        searchResults.map((f: Filter, i: number) => (
          <div
            key={filterRowKey(f, 's', i)}
            className="result-row"
            onClick={() => pickFilter(f)}
          >
            <div className="left">
              <span className="filter-name">{renderFilterName(f.presentation.name)}</span>
              {f.presentation.isNew && <span className="new-badge">NEW</span>}
            </div>
            <span className="category-hint">{f.presentation.newCategory}</span>
          </div>
        ))
      )}
    </div>
  )
}

function QuickFilters({ quickFilters, pickFilter }) {
  if (quickFilters.length === 0) return null
  return (
    <div className="quick-filters">
      <div className="section-header">
        <SvgIconsStar width={12} height={12} />
        <span>Quick filters</span>
      </div>
      <div className="chips">
        {quickFilters.map((f: Filter, i: number) => (
          <span
            key={filterRowKey(f, 'q', i)}
            className="chip"
            onClick={() => pickFilter(f)}
          >
            {renderFilterName(f.presentation.name)}
          </span>
        ))}
      </div>
    </div>
  )
}

function CategoryRail({
  visibleCategories,
  filtersByCategory,
  selectedCategory,
  setSelectedCategory,
}) {
  return (
    <div className="left-rail">
      {visibleCategories.map((cat: Category, i: number) => {
        const prev = i > 0 ? visibleCategories[i - 1] : null
        const showParent = !prev || prev.parent !== cat.parent
        const showSubheading =
          cat.subheading && (!prev || prev.subheading !== cat.subheading)
        const filtersInCat = filtersByCategory[cat.name] || []
        const hasNew = filtersInCat.some((f: Filter) => f.presentation.isNew)
        const isActive = selectedCategory === cat.name

        return (
          <div key={cat.name}>
            {showParent && <div className="parent-question">{cat.parent}</div>}
            {showSubheading && <div className="subheading">{cat.subheading}</div>}
            <div
              className={`category-row ${isActive ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.name)}
            >
              <span className="cat-icon">{cat.icon}</span>
              <span className="cat-name">{cat.name}</span>
              {hasNew && <span className="new-dot" />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function CategoryPane({ activeCategory, activeFilters, pickFilter }) {
  return (
    <div className="right-pane">
      {!activeCategory && (
        <div className="empty-pane">
          <div className="hand">👈</div>
          <div>Select a category to browse filters</div>
        </div>
      )}
      {activeCategory && (
        <>
          <div className="pane-header">
            <span className="cat-icon">{activeCategory.icon}</span>
            <span className="cat-name">{activeCategory.name}</span>
            <span className="count">{activeFilters.length}</span>
          </div>
          {activeFilters.length === 0 && (
            <div className="empty">No filters in this category yet</div>
          )}
          {activeFilters.map((f: Filter, i: number) => (
            <div
              key={filterRowKey(f, 'f', i)}
              className="filter-row"
              onClick={() => pickFilter(f)}
            >
              <span className="filter-name">{renderFilterName(f.presentation.name)}</span>
              {f.presentation.isNew && <span className="new-badge">NEW</span>}
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default function FilterList2({ groupId, close }) {
  const act = useAct()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(handle)
  }, [search])

  const availableFilters = useSel((s) => s.availableFilters)
  const newCategories: Category[] = useSel((s) => s.local.newCategories || [])
  const featuredCustomFields = useSel((s) => s.local.featuredCustomFields || [])

  const featuredCustomFieldsFilters: Filter[] = featuredCustomFields.map((cf) => ({
    customFieldId: cf.id,
    kind: cf.kind,
    fields: {},
    presentation: {
      id: 'featuredCustomField',
      name: cf.name,
      group: 'Quick',
      newCategory: 'Custom Fields',
      isNew: false,
      keywords: [],
      hidden: false,
    },
  }))

  const visibleFilters: Filter[] = Object.values(availableFilters).filter(
    (filter: any) => !filter.presentation.hidden
  )

  const expandedVisibleFilters: Filter[] = visibleFilters.flatMap((filter) => {
    if (filter.modes && filter.modes.length > 0) {
      return filter.modes.map((mode) => ({
        ...filter,
        _mode: mode,
        presentation: {
          ...filter.presentation,
          name: mode.name,
          group: mode.group || filter.presentation.group,
          quickOrder: mode.quickOrder,
          newCategory: mode.newCategory || filter.presentation.newCategory,
        },
      }))
    }
    return [filter]
  })

  const allFilters: Filter[] = [
    ...expandedVisibleFilters,
    ...featuredCustomFieldsFilters,
  ]

  const filtersByCategory: Record<string, Filter[]> = _.groupBy(
    allFilters,
    (f) => f.presentation.newCategory || ''
  )

  Object.keys(filtersByCategory).forEach((cat) => {
    filtersByCategory[cat] = _.sortBy(filtersByCategory[cat], (f) => f.presentation.name.toLowerCase())
  })

  const quickFilters = _.sortBy(
    allFilters.filter((f) => typeof f.presentation.quickOrder === 'number'),
    (f) => f.presentation.quickOrder as number
  )

  const visibleCategories = newCategories.filter(
    (cat) => (filtersByCategory[cat.name] || []).length > 0
  )

  const isSearchActive = search.trim().length > 1
  const searchTerm = debouncedSearch.trim()
  const isAwaitingDebounce = isSearchActive && searchTerm.length <= 1
  const searchResults: Filter[] = searchTerm.length > 1
    ? _.uniqBy(
        fuzzysort.go(stemQuery(searchTerm), allFilters, {
          keys: [
            (f: Filter) => stemQuery(f.presentation.name),
            (f: Filter) => stemQuery((f.presentation.keywords || []).join(' ')),
          ],
          threshold: 0.2,
          limit: 20,
        }).map((r) => r.obj),
        (f) => `${f.presentation.id}-${f._mode?.name || ''}-${f.customFieldId || ''}`
      )
    : []
  const hasNoSearchResults =
    isSearchActive && !isAwaitingDebounce && searchResults.length === 0

  function addFilter(filter: Filter) {
    const id = filter.presentation.id
    if (id === 'featuredCustomField') {
      const filterType = filter.kind === 'float' ? 'numberCustomField' : 'customField'
      act.search.addFilter({
        parentRuleId: groupId,
        filter: filterType,
        parameters: { customFieldId: filter.customFieldId },
      })
    } else if (filter._mode) {
      const defaults = { ...filter._mode.defaults }
      const ruleOverrides: Record<string, any> = {}
      if (defaults.not !== undefined) {
        ruleOverrides.not = defaults.not
        delete defaults.not
      }
      act.search.addFilter({
        parentRuleId: groupId,
        filter: id,
        parameters: defaults,
        ruleOverrides,
      })
    } else {
      act.search.addFilter({ parentRuleId: groupId, filter: id })
    }
    trackEvent('audience_builder_added_rule', {
      type: 'filter',
      filterId: id,
    })
  }

  function pickFilter(filter: Filter) {
    addFilter(filter)
    close()
  }

  const activeCategory = newCategories.find((c) => c.name === selectedCategory)
  const activeFilters = activeCategory ? (filtersByCategory[activeCategory.name] || []) : []

  return (
    <div className="filter-picker">
      <PickerSearchInput
        search={search}
        setSearch={setSearch}
        close={close}
        inputRef={inputRef}
      />

      <div className="picker-body">
        {isSearchActive ? (
          <SearchResults
            isAwaitingDebounce={isAwaitingDebounce}
            hasNoSearchResults={hasNoSearchResults}
            searchResults={searchResults}
            debouncedSearch={debouncedSearch}
            pickFilter={pickFilter}
          />
        ) : (
          <>
            <QuickFilters quickFilters={quickFilters} pickFilter={pickFilter} />
            <div className="browser">
              <CategoryRail
                visibleCategories={visibleCategories}
                filtersByCategory={filtersByCategory}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
              <CategoryPane
                activeCategory={activeCategory}
                activeFilters={activeFilters}
                pickFilter={pickFilter}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
